import type { AxiosResponse } from 'axios'
import { z } from 'zod'
import type { RtnCode } from '@library/enums'

export const paginationSchema = z.object({
  currentPage: z.number(),
  pageSize: z.number(),
  total: z.number()
})

export const sortSchema = z.object({
  orderBy: z.union([z.literal('asc'), z.literal('desc')]),
  sortBy: z.string()
})

export const responseDataSchema = z.object({
  rtnCode: z.number().optional(),
  message: z.string(),
  pagination: paginationSchema.nullable(),
  sort: z.array(sortSchema).nullable(),
  data: z.unknown()
})

export const apiResponseSchema = z.object({
  data: responseDataSchema,
  status: z.number(),
  statusText: z.string(),
  headers: z.unknown(),
  config: z.unknown(),
  request: z.unknown().optional()
})

export interface ResponseSuccessData<T = any>
  extends z.infer<typeof responseDataSchema> {
  rtnCode: 0
  data: T
}

type FailData<T extends RtnCode, U> = Omit<
  z.infer<typeof responseDataSchema>,
  'rtnCode'
> & {
  rtnCode?: T
  data: U
}

export type ResponseFailData =
  | FailData<Exclude<RtnCode, 0 | 10 | 46>, null>
  | FailData<10 | 46, { errorCount: number }>

export type ResponseData<T = any> = ResponseSuccessData<T> | ResponseFailData

export interface ApiResponse<T = any>
  extends z.infer<typeof apiResponseSchema> {
  data: ResponseData<T>
  config: AxiosResponse['config']
  headers: AxiosResponse['headers']
  request?: AxiosResponse['request']
}
